import {
  CallScribeCustomerEmotion,
  CallScribeSentimentEngine,
  CallScribeSentimentGate,
  ISentimentJudgment,
  ISentimentJudgmentTurn,
  SentimentJudgmentEmotionName,
  SentimentJudgmentSpeakerName,
} from 'services/callScribe/types';

export const EMOTION_STRENGTH: number[] = [0.4, 0.6, 0.8, 1.0];
export const CARRY_STRENGTH = 0.16;

export const STRENGTH_WORDS: Record<number, string> = {
  0: 'faint',
  1: 'mild',
  2: 'clear',
};

export const SPECTRUM_EMOTIONS: SentimentJudgmentEmotionName[] = [
  'Satisfied',
  'Neutral',
  'Confused',
  'Anxious',
  'Frustrated',
  'Angry',
];

export const TRIGGER_DISPLAY: Record<string, string> = {
  asks_for_manager: 'Asked for a manager',
  threatens_cancel: 'Threatened to cancel',
  repeat_contact: 'Repeat contact',
  blames_msp: 'Blamed the MSP',
  unresolved_at_end: 'Unresolved at the end',
};

const CUSTOMER_EMOTION_LABEL: Record<CallScribeCustomerEmotion, string> = {
  [CallScribeCustomerEmotion.Unknown]: 'Unknown',
  [CallScribeCustomerEmotion.Neutral]: 'Neutral',
  [CallScribeCustomerEmotion.Satisfied]: 'Satisfied',
  [CallScribeCustomerEmotion.Confused]: 'Confused',
  [CallScribeCustomerEmotion.Anxious]: 'Anxious',
  [CallScribeCustomerEmotion.Frustrated]: 'Frustrated',
  [CallScribeCustomerEmotion.Angry]: 'Angry',
};

const ESCALATION_RISK_LABEL: Record<number, string> = {
  0: 'None',
  1: 'Low',
  2: 'Medium',
  3: 'High',
};

const REFUSAL_GATES = new Set<CallScribeSentimentGate>([
  CallScribeSentimentGate.InsufficientCustomerSpeech,
  CallScribeSentimentGate.NoCustomerEvidence,
  CallScribeSentimentGate.LowConfidence,
  CallScribeSentimentGate.ValidationRejected,
]);

export const formatClock = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const resolveDurationMs = (
  recordingDurationSeconds: number | null | undefined,
  durationSeconds: number | null | undefined,
  judgment: ISentimentJudgment | null,
): number => {
  if (recordingDurationSeconds != null) {
    return recordingDurationSeconds * 1000;
  }
  if (durationSeconds != null) {
    return durationSeconds * 1000;
  }
  if (judgment && judgment.turns.length > 0) {
    return judgment.turns[judgment.turns.length - 1].endMs + (judgment.audioOffsetMs ?? 0);
  }
  return 0;
};

export interface AxisTick {
  ms: number;
  label: string;
  isEnd: boolean;
}

const axisStepMs = (durationMs: number): number => {
  if (durationMs <= 5 * 60_000) return 30_000;
  if (durationMs <= 30 * 60_000) return 60_000;
  return 300_000;
};

export const buildAxisTicks = (durationMs: number): AxisTick[] => {
  const step = axisStepMs(durationMs);
  const ticks: number[] = [];
  for (let ms = 0; ms < durationMs; ms += step) {
    if (ms > 0 && durationMs - ms < durationMs * 0.08) break;
    ticks.push(ms);
  }
  ticks.push(durationMs);
  return ticks.map((ms, i) => ({
    ms,
    label: formatClock(ms),
    isEnd: i === ticks.length - 1,
  }));
};

export const thinTicks = (ticks: AxisTick[], max: number): AxisTick[] => {
  if (ticks.length <= max) return ticks;
  const inner = ticks.slice(1, -1);
  const keep = Math.max(0, max - 2);
  const picked = Array.from({ length: keep }, (_, i) => inner[Math.round(((i + 1) * (inner.length + 1)) / (keep + 1)) - 1])
    .filter((t): t is AxisTick => Boolean(t));
  return [ticks[0], ...picked, ticks[ticks.length - 1]];
};

export interface SpectrumSegment {
  kind: 'turn' | 'carry';
  turnId: string;
  startMs: number;
  endMs: number;
  emotion: SentimentJudgmentEmotionName;
  intensity: number;
  strength: number;
  quote: string;
  cues: string[];
}

const shiftAndClampTurns = (
  judgment: ISentimentJudgment,
  speaker: SentimentJudgmentSpeakerName,
  durationMs: number,
): ISentimentJudgmentTurn[] => {
  const offset = judgment.audioOffsetMs ?? 0;
  return judgment.turns
    .filter(t => t.speaker === speaker)
    .map(t => ({
      ...t,
      startMs: Math.max(0, t.startMs + offset),
      endMs: Math.min(durationMs, t.endMs + offset),
    }))
    .filter(t => t.startMs < durationMs && t.endMs > 0 && t.endMs > t.startMs)
    .sort((a, b) => a.startMs - b.startMs);
};

export const buildEmotionStory = (
  judgment: ISentimentJudgment,
  durationMs: number,
  speaker: SentimentJudgmentSpeakerName = 'customer',
): string => {
  const turns = shiftAndClampTurns(judgment, speaker, durationMs);
  if (turns.length === 0) return '';
  const first = turns[0].emotion;
  const last = turns[turns.length - 1].emotion;
  return first === last
    ? `${first} throughout`
    : `Started ${first.toLowerCase()} → ended ${last.toLowerCase()}`;
};

export const buildLaneSegments = (
  judgment: ISentimentJudgment,
  speaker: SentimentJudgmentSpeakerName,
  durationMs: number,
): SpectrumSegment[] => {
  const turns = shiftAndClampTurns(judgment, speaker, durationMs);
  const segments: SpectrumSegment[] = [];

  turns.forEach((t, i) => {
    segments.push({
      kind: 'turn',
      turnId: t.turnId,
      startMs: t.startMs,
      endMs: t.endMs,
      emotion: t.emotion,
      intensity: t.intensity,
      strength: EMOTION_STRENGTH[t.intensity] ?? 1,
      quote: t.quote,
      cues: t.cues ?? [],
    });
    const nextStart = i + 1 < turns.length ? turns[i + 1].startMs : durationMs;
    if (nextStart > t.endMs) {
      segments.push({
        kind: 'carry',
        turnId: t.turnId,
        startMs: t.endMs,
        endMs: nextStart,
        emotion: t.emotion,
        intensity: t.intensity,
        strength: CARRY_STRENGTH,
        quote: t.quote,
        cues: [],
      });
    }
  });

  return segments;
};

export interface TriggerFlag {
  trigger: string;
  label: string;
  atMs: number;
  quote: string;
}

export const buildTriggerFlags = (judgment: ISentimentJudgment): TriggerFlag[] => {
  const offset = judgment.audioOffsetMs ?? 0;
  const byId = new Map(judgment.turns.map(t => [t.turnId, t]));
  const flags: TriggerFlag[] = [];

  for (const trigger of judgment.customerState?.triggers ?? []) {
    const candidates = trigger.turnIds
      .map(id => byId.get(id))
      .filter((t): t is ISentimentJudgmentTurn => !!t);
    const evidence = candidates.filter(t => t.isEvidence);
    const pool = evidence.length > 0 ? evidence : candidates;
    if (pool.length === 0) continue;
    const earliest = pool.reduce((a, b) => (b.startMs < a.startMs ? b : a));
    flags.push({
      trigger: trigger.trigger,
      label: TRIGGER_DISPLAY[trigger.trigger] ?? trigger.trigger,
      atMs: earliest.startMs + offset,
      quote: earliest.quote,
    });
  }

  return flags;
};

export const buildSpanSummary = (
  judgment: ISentimentJudgment,
  speaker: SentimentJudgmentSpeakerName,
  durationMs: number,
): string => {
  const turns = shiftAndClampTurns(judgment, speaker, durationMs);
  const spans: Array<{ emotion: SentimentJudgmentEmotionName; startMs: number; endMs: number }> = [];

  turns.forEach(t => {
    const last = spans[spans.length - 1];
    if (last && last.emotion === t.emotion) {
      last.endMs = t.endMs;
    } else {
      spans.push({ emotion: t.emotion, startMs: t.startMs, endMs: t.endMs });
    }
  });

  return spans
    .map(s => `${s.emotion} ${formatClock(s.startMs)}–${formatClock(s.endMs)}`)
    .join(' · ');
};

export interface InsightsHeaderInput {
  sentimentEngine: CallScribeSentimentEngine;
  sentimentGate: CallScribeSentimentGate | null;
  customerStartingState: CallScribeCustomerEmotion | null;
  customerEndingState: CallScribeCustomerEmotion | null;
  escalationRisk: number | null;
}

export type InsightsHeader =
  | { kind: 'judged' | 'refused'; text: string }
  | { kind: 'legacy'; text?: undefined };

export const buildInsightsHeader = (call: InsightsHeaderInput): InsightsHeader => {
  const isRefusal = call.sentimentGate != null && REFUSAL_GATES.has(call.sentimentGate);
  if (isRefusal) {
    return { kind: 'refused', text: 'Not enough evidence' };
  }
  if (call.sentimentEngine !== CallScribeSentimentEngine.GeminiAudioV2) {
    return { kind: 'legacy' };
  }

  const endLabel = CUSTOMER_EMOTION_LABEL[call.customerEndingState ?? CallScribeCustomerEmotion.Unknown] ?? 'Unknown';
  const startLabel = call.customerStartingState != null ? CUSTOMER_EMOTION_LABEL[call.customerStartingState] : null;
  const riskLabel = ESCALATION_RISK_LABEL[call.escalationRisk ?? -1] ?? 'None';
  const emotionText = startLabel && startLabel !== endLabel ? `${startLabel} → ${endLabel}` : endLabel;

  return { kind: 'judged', text: `Customer: ${emotionText} · Escalation risk ${riskLabel}` };
};
